import React, { useState } from 'react'
import { Box, Chip, Tooltip } from '@mui/material'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import { type IAttribute } from '../../../../models/Division'
import { grey } from '../../../../colors'
import { ATTRIBUTE_TYPES, ATTRIBUTE_TYPE_CHIP } from '../../../../consts'

const CHIP_VISIBLE_LIMIT = 3

export type AttributeSurfaceKind = 'section' | 'user'
export type AttributeLineVariant = 'block' | 'inline'

const HELP_COPY: Record<AttributeSurfaceKind, string> = {
  section: 'Course Section Attributes',
  user: 'User Attributes'
}

const CHIP_PALETTE: Record<AttributeSurfaceKind, { bg: string, fg: string }> = {
  section: ATTRIBUTE_TYPE_CHIP[ATTRIBUTE_TYPES.COURSE_ATTRIBUTES],
  user: ATTRIBUTE_TYPE_CHIP[ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES]
}

interface ReportAttributeLineProps {
  attributes?: IAttribute[]
  surfaceKind: AttributeSurfaceKind
  variant?: AttributeLineVariant
}

const AttributeChip: React.FC<{ attr: IAttribute, palette: { bg: string, fg: string } }> = ({ attr, palette }) => (
  <Tooltip title={`${attr.title}: ${attr.value}`} arrow>
    <Chip
      size="small"
      label={attr.value}
      sx={{ bgcolor: palette.bg, color: palette.fg, fontWeight: 500 }}
    />
  </Tooltip>
)

const ReportAttributeLine: React.FC<ReportAttributeLineProps> = ({ attributes, surfaceKind, variant = 'block' }) => {
  const [expanded, setExpanded] = useState(false)
  const list = attributes ?? []
  if (list.length === 0) return null

  const palette = CHIP_PALETTE[surfaceKind]
  const isOverLimit = list.length > CHIP_VISIBLE_LIMIT
  const visible = expanded || !isOverLimit ? list : list.slice(0, CHIP_VISIBLE_LIMIT)
  const hiddenCount = list.length - CHIP_VISIBLE_LIMIT
  const isInline = variant === 'inline'

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75} mb={isInline ? 0 : 1.5}>
      <Tooltip title={HELP_COPY[surfaceKind]} placement="bottom" arrow>
        <LabelOutlinedIcon
          aria-label={HELP_COPY[surfaceKind]}
          sx={{ color: 'iconColor', mr: 0.25 }}
        />
      </Tooltip>
      {visible.map((attr, idx) => (
        <AttributeChip key={`${attr.title}-${attr.value}-${idx}`} attr={attr} palette={palette} />
      ))}
      {isOverLimit && (
        <Chip
          size="small"
          label={expanded ? 'Show less' : `+${hiddenCount} more`}
          onClick={() => { setExpanded(prev => !prev) }}
          aria-expanded={expanded}
          sx={{
            bgcolor: grey[200],
            color: grey[800],
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { bgcolor: grey[300] }
          }}
        />
      )}
    </Box>
  )
}

export default ReportAttributeLine
